import React, { useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { shouldRenderVideoEmbed } from '../../../config/videoEmbeds';
import ctaVideo from '../../../assets/videos/desktop-home-cta.mp4';
import ctaMobileVideo from '../../../assets/videos/mobile-home-cta.mp4';
import './DontMissOut.css';

/**
 * Full-bleed CTA section with optional looping background video by breakpoint.
 * Exports:
 * - default: React component; skips the `<video>` element when disabled in videoEmbeds config.
 *   @param {{ onSectionClick: () => void }} props - Section click handler.
 */
const DontMissOut = ({ onSectionClick }) => {
  const showVideo = shouldRenderVideoEmbed('dontMissOut');
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const playTimeoutRef = useRef(null);
  const isInView = useInView(containerRef, { amount: 0.5, once: false });
  const isMobile = useIsMobile();

  // Log when video source changes
  useEffect(() => {
    console.log('Current video source:', isMobile ? 'mobile' : 'desktop');
    
    // Force video reload when source changes
    if (showVideo && videoRef.current) {
      videoRef.current.load();
    }
  }, [isMobile, showVideo]);

  useEffect(() => {
    if (!showVideo) {
      return;
    }
    if (videoRef.current) {
      if (isInView) {
        // Reset video immediately
        videoRef.current.currentTime = 0;
        
        // Clear any existing timeout
        if (playTimeoutRef.current) {
          clearTimeout(playTimeoutRef.current);
        }
        
        // Start playing after 2 seconds
        playTimeoutRef.current = setTimeout(() => {
          videoRef.current.play().catch(error => {
            console.log("Video autoplay failed:", error);
          });
        }, 2000);
      } else {
        // Clear timeout if section scrolls out of view
        if (playTimeoutRef.current) {
          clearTimeout(playTimeoutRef.current);
        }
        videoRef.current.pause();
      }
    }

    // Cleanup timeout on unmount or when effect re-runs
    return () => {
      if (playTimeoutRef.current) {
        clearTimeout(playTimeoutRef.current);
      }
    };
  }, [isInView, showVideo]);

  const videoSource = isMobile ? ctaMobileVideo : ctaVideo;
  console.log('Video source:', videoSource);

  return (
    <section 
      id="dont-miss-out" 
      className="dont-miss-out-section"
      onClick={onSectionClick}
      style={{ cursor: 'pointer' }}
    >
      <motion.div 
        ref={containerRef}
        className="video-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 0.5 }}
      >
        {showVideo && (
          <video
            ref={videoRef}
            className="full-width-video"
            playsInline
            muted
            key={videoSource}
          >
            <source src={videoSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </motion.div>
    </section>
  );
};

export default DontMissOut; 